import React, { useCallback, useEffect, useMemo, useState } from "react";
import ReactECharts from "echarts-for-react";
import { runNrql } from "../nr1/nerdgraph.js";
import { useChartTheme, withChartTheme } from "../nerdlets/home/charts/theme";
import LogEventDetail from "./LogEventDetail";
import { addFragment } from "./nrqlFilter";

/**
 * Resource usage and recent logs for an infrastructure host.
 *
 * CPU, memory and disk all come from a single SystemSample query — they are
 * three fields on one event type, and all three are percentages, so one chart
 * with one 0–100 axis says more than three sparklines would.
 *
 * Both panels always render, including when a query legitimately returns
 * nothing: logging is enabled per host, so "no logs" is a real finding about
 * this host rather than a gap in the view. A zero is a measurement.
 */

const RANGES = [
  { label: "30 min", since: "30 minutes ago" },
  { label: "3 hours", since: "3 hours ago" },
  { label: "24 hours", since: "24 hours ago" },
  { label: "7 days", since: "7 days ago" },
];

/** NRQL string literals are single-quoted, so a quote in the value would escape. */
const nrqlString = (value) => `'${String(value).replace(/'/g, "\\'")}'`;

/**
 * Turns whatever the user typed into a log query.
 *
 * Three cases, in order:
 *   empty            the default: this host's last 10 records
 *   starts SELECT/FROM   used verbatim, so the box is a real NRQL console
 *   anything else    a filter. A bare word searches the message; anything
 *                    containing an operator is treated as a WHERE fragment.
 *
 * The fragment is parenthesised and ANDed after the hostname clause so a stray
 * `OR` cannot widen the query off this host — the panel would otherwise start
 * showing another machine's logs under this one's heading.
 */
export function buildLogQuery(host, since, input) {
  const text = (input || "").trim();
  // Every attribute, not the three the table shows: clicking a row expands the
  // full event, and carrying it here means that costs no extra query and can
  // never show a different record than the one clicked. A row is ~750 bytes,
  // so 50 of them is around 37kB.
  const base = `SELECT * FROM Log WHERE hostname = ${host}`;

  if (!text) return `${base} SINCE ${since} LIMIT 10`;
  if (/^\s*(SELECT|FROM)\b/i.test(text)) return text;

  const isExpression = /[=<>]|\bLIKE\b|\bIN\b|\bIS\b|\bRLIKE\b/i.test(text);
  const clause = isExpression
    ? text
    : `message LIKE '%${text.replace(/'/g, "\\'")}%'`;

  return `${base} AND (${clause}) SINCE ${since} LIMIT 50`;
}

/** Whether a result set is actually log records, or some other query's shape. */
function isLogShape(rows) {
  const row = rows[0] || {};
  return "timestamp" in row && "message" in row;
}

function formatCell(value) {
  if (value === null || value === undefined) return "—";
  if (typeof value === "number") return Number(value.toFixed(3)).toString();
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

const SERIES = [
  { key: "cpu", name: "CPU", color: "#3ED2F0" },
  { key: "mem", name: "Memory", color: "#A45AC1" },
  { key: "disk", name: "Disk", color: "#FFC400" },
];

function useHostQuery(accountId, nrql, deps) {
  const [state, setState] = useState({ rows: null, error: null, loading: true });

  useEffect(() => {
    if (!accountId || !nrql) return undefined;
    const controller = new AbortController();
    setState({ rows: null, error: null, loading: true });

    runNrql({ accountId, query: nrql, signal: controller.signal })
      .then((result) =>
        setState({ rows: result.rawResults || [], error: null, loading: false })
      )
      .catch((error) => {
        if (error?.name === "AbortError") return;
        setState({ rows: null, error, loading: false });
      });

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return state;
}

export default function HostPanels({ hostname, accountId }) {
  const [since, setSince] = useState(RANGES[1].since);
  const theme = useChartTheme();

  const host = nrqlString(hostname);

  const metrics = useHostQuery(
    accountId,
    `SELECT average(cpuPercent) AS cpu, average(memoryUsedPercent) AS mem, average(diskUsedPercent) AS disk ` +
      `FROM SystemSample WHERE hostname = ${host} TIMESERIES SINCE ${since}`,
    [accountId, hostname, since]
  );

  // Typed text is held separately from the applied query so every keystroke
  // does not fire NRQL; the query runs on Enter or blur.
  const [draft, setDraft] = useState("");
  const [applied, setApplied] = useState("");

  // One row open at a time. Several expanded events would bury the list they
  // were opened from, which is the thing inline expansion exists to preserve.
  const [openLog, setOpenLog] = useState(null);

  // A different query or window returns different rows, so whatever was open
  // no longer refers to anything on screen.
  useEffect(() => setOpenLog(null), [applied, since]);

  /**
   * A filter chosen from an attribute menu lands in the same box a human types
   * in, and is shown there. Applying it invisibly would leave the reader with a
   * changed list and no way to see why, or to undo it.
   */
  const applyFragment = useCallback(
    (fragment) => {
      if (!fragment) return;
      setDraft((current) => {
        const next = addFragment(current, fragment);
        setApplied(next);
        return next;
      });
    },
    []
  );

  const logQuery = buildLogQuery(host, since, applied);
  const logs = useHostQuery(accountId, logQuery, [
    accountId,
    hostname,
    since,
    applied,
  ]);

  const option = useMemo(() => {
    const rows = metrics.rows || [];
    if (!rows.length) return null;
    return withChartTheme(
      {
        grid: { left: 44, right: 16, top: 28, bottom: 28 },
        tooltip: { trigger: "axis" },
        legend: { data: SERIES.map((s) => s.name) },
        xAxis: {
          type: "time",
          axisLabel: {
            formatter: (value) =>
              new Date(value).toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              }),
          },
        },
        // Shared axis: all three are percentages, which is the whole reason
        // they belong on one chart.
        yAxis: { type: "value", min: 0, max: 100, axisLabel: { formatter: "{value}%" } },
        series: SERIES.map((s) => ({
          name: s.name,
          type: "line",
          showSymbol: false,
          smooth: true,
          itemStyle: { color: s.color },
          lineStyle: { color: s.color, width: 2 },
          data: rows
            .filter((r) => r[s.key] !== null && r[s.key] !== undefined)
            .map((r) => [r.beginTimeSeconds * 1000, Number(r[s.key].toFixed(2))]),
        })),
      },
      theme
    );
  }, [metrics.rows, theme]);

  const latest = useMemo(() => {
    const rows = (metrics.rows || []).filter((r) => r.cpu !== null);
    return rows.length ? rows[rows.length - 1] : null;
  }, [metrics.rows]);

  return (
    <section className="host-panels">
      <header className="host-panels__head">
        <h3>Host resources</h3>
        <div className="host-range" role="group" aria-label="Time range">
          {RANGES.map((r) => (
            <button
              key={r.since}
              type="button"
              className={`host-range__btn${since === r.since ? " is-active" : ""}`}
              aria-pressed={since === r.since}
              onClick={() => setSince(r.since)}
            >
              {r.label}
            </button>
          ))}
        </div>
      </header>

      <div className="host-card">
        {/* Always rendered, empty when there is no reading yet. Hiding it while
            a new range loaded took 24px out of the card — the last of the jump
            once the chart slot below stopped collapsing. */}
        <div className="host-latest">
          {latest
            ? SERIES.map((s) => (
                <span className="host-latest__item" key={s.key}>
                  <i style={{ background: s.color }} />
                  {s.name} <strong>{Number(latest[s.key]).toFixed(1)}%</strong>
                </span>
              ))
            : null}
        </div>

        {/* Every state fills the same box as the chart. Swapping a 260px chart
            for a one-line paragraph collapsed the card from 307px to 54px and
            back on every range change — measured, not guessed. */}
        {metrics.loading ? (
          <div className="host-slot" aria-busy="true">
            <p className="host-note">Loading…</p>
          </div>
        ) : metrics.error ? (
          <div className="host-slot">
            <p className="host-note host-note--bad">
              Could not read metrics: {metrics.error.message}
            </p>
          </div>
        ) : option ? (
          <ReactECharts
            option={option}
            style={{ height: 260, width: "100%" }}
            notMerge
            opts={{ renderer: "canvas" }}
            // The panel lays out after this mounts, so echarts measures a
            // zero-width container and draws nothing. One resize once the
            // instance exists is enough to make it pick up the real box.
            onChartReady={(chart) => {
              requestAnimationFrame(() => chart.resize());
            }}
          />
        ) : (
          <div className="host-slot">
            <p className="host-note">
              No SystemSample data in this window — the infrastructure agent may
              not be reporting for this host.
            </p>
          </div>
        )}
      </div>

      <header className="host-panels__head">
        <h3>
          Recent logs{" "}
          <span className="host-count">
            {logs.rows ? logs.rows.length : ""}
          </span>
        </h3>
        <input
          className="host-search"
          type="search"
          value={draft}
          placeholder="filter or NRQL…"
          aria-label="Filter logs, or type an NRQL query"
          title={"A word searches the message.\nAn expression like level = 'ERROR' becomes a filter.\nStarting with SELECT or FROM runs your query as-is."}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") setApplied(draft);
            if (e.key === "Escape") {
              setDraft("");
              setApplied("");
            }
          }}
          onBlur={() => setApplied(draft)}
        />
      </header>

      <div className="host-card">
        {applied ? (
          // What actually ran, so a surprising result is debuggable rather
          // than mysterious.
          <code className="host-query" title={logQuery}>
            {logQuery}
          </code>
        ) : null}

        {logs.loading ? (
          // Reserved rather than exact: the loaded height depends on how many
          // rows came back. Enough to stop the card snapping shut while a
          // filter runs.
          <div className="host-slot host-slot--logs" aria-busy="true">
            <p className="host-note">Loading…</p>
          </div>
        ) : logs.error ? (
          <div className="host-slot host-slot--logs">
            <p className="host-note host-note--bad">
              Could not read logs: {logs.error.message}
            </p>
          </div>
        ) : logs.rows && logs.rows.length ? (
          isLogShape(logs.rows) ? (
            <table className="host-logs host-logs--expandable">
              <tbody>
                {logs.rows.map((row, i) => {
                  const key = `${row.timestamp}-${i}`;
                  const isOpen = openLog === key;
                  // Columns vary — level is only rendered when present — so
                  // count them rather than hard-coding the span.
                  const columns = row.level ? 3 : 2;
                  return (
                    <React.Fragment key={key}>
                      <tr
                        className={`host-logs__row${isOpen ? " is-open" : ""}`}
                        role="button"
                        tabIndex={0}
                        aria-expanded={isOpen}
                        onClick={() => setOpenLog(isOpen ? null : key)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            setOpenLog(isOpen ? null : key);
                          }
                        }}
                      >
                        <td className="host-logs__time">
                          {new Date(row.timestamp).toLocaleString()}
                        </td>
                        {row.level ? (
                          <td className="host-logs__level">{String(row.level)}</td>
                        ) : null}
                        <td className="host-logs__msg">{row.message ?? "—"}</td>
                      </tr>
                      {isOpen ? (
                        <tr className="host-logs__detail-row">
                          <td colSpan={columns}>
                            <LogEventDetail
                              event={row}
                              onClose={() => setOpenLog(null)}
                              onFilter={applyFragment}
                            />
                          </td>
                        </tr>
                      ) : null}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          ) : (
            // A typed query can return any shape at all — a FACET count has no
            // timestamp or message — so fall back to showing whatever columns
            // came back rather than rendering "Invalid Date" for each row.
            <table className="host-logs">
              <thead>
                <tr>
                  {Object.keys(logs.rows[0]).map((col) => (
                    <th key={col} className="host-logs__time">
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {logs.rows.map((row, i) => (
                  <tr key={i}>
                    {Object.keys(logs.rows[0]).map((col) => (
                      <td key={col} className="host-logs__msg">
                        {formatCell(row[col])}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )
        ) : (
          // Deliberately shown rather than hidden: logging is per host, so a
          // count of zero tells you this host forwards nothing, which is worth
          // knowing and is not the same as the query having failed.
          <div className="host-slot host-slot--logs">
            <p className="host-note">
              <strong>0</strong> log records
              {applied ? " matching that query" : " in this window"}. Logging is
              enabled per host, so this host may not be forwarding logs.
            </p>
          </div>
        )}
      </div>
    </section>
  );
}
